import { defineStore } from 'pinia'
import axios from 'axios'
import type { UserInfo } from '@/types'

// A tenant switch into another customer that waits for the user's confirmation.
export interface PendingTenantSwitch {
  customerNo: string
  customerName?: string
  tenantName: string
  redirectPath?: string
}

import { useAddressStore } from '@/stores/address'
import { useProductStore } from '@/stores/product'
import { useProjectStore } from '@/stores/project'
import { useTransactionStore } from '@/stores/transaction'
import { getActivePinia } from 'pinia'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    userInfo: null as UserInfo | null,
    loading: false,
    error: null as Error | null,
    pendingTenantSwitch: null as PendingTenantSwitch | null,
  }),
  getters: {
    isLoggedIn: (state) => !!state.userInfo,
    allowedTenants: (state) => state.userInfo?.allowedTenants || [],
  },
  actions: {
    async fetchUserInfo() {
      this.loading = true
      this.error = null
      try {
        axios.defaults.withCredentials = true
        axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
        const response = await axios.get('/api/registry/v1/user-info')
        this.userInfo = response.data
        return this.userInfo
      } catch (err) {
        this.error = err as Error
        this.userInfo = null
        throw err
      } finally {
        this.loading = false
      }
    },
    async switchTenant(customerNo: string, tenantName: string, redirectPath?: string) {
      this.loading = true
      try {
        await axios.post('/api/registry/v1/auth/switch-tenant', {
          customerNo,
          tenantName,
        })
        await this.fetchUserInfo()
        const channel = new BroadcastChannel('auth-events')
        channel.postMessage({
          type: 'TENANT_SWITCHED',
          customerNo,
          tenantName,
        })
        channel.close()
        if (redirectPath) {
          window.location.href = redirectPath
        } else {
          window.location.reload() // Reload to ensure all components refresh with new tenant context
        }
      } catch (err) {
        console.error('Failed to switch tenant', err)
        throw err
      } finally {
        this.loading = false
      }
    },
    // Entry point of every interactive tenant switch: a switch within the active customer happens
    // right away, a switch into another customer waits for confirmTenantSwitch (the registry
    // records it in that customer's access log).
    async requestTenantSwitch(customerNo: string, tenantName: string, redirectPath?: string) {
      if (customerNo === this.userInfo?.activeTenant?.customerNo) {
        await this.switchTenant(customerNo, tenantName, redirectPath)
        return
      }
      const customerName = this.allowedTenants.find((t) => t.customerNo === customerNo)?.customerName
      this.pendingTenantSwitch = { customerNo, customerName, tenantName, redirectPath }
    },
    async confirmTenantSwitch() {
      const pending = this.pendingTenantSwitch
      if (!pending) return
      try {
        await this.switchTenant(pending.customerNo, pending.tenantName, pending.redirectPath)
      } finally {
        this.pendingTenantSwitch = null
      }
    },
    cancelTenantSwitch() {
      this.pendingTenantSwitch = null
    },
    async refreshSession() {
      try {
        await axios.post('/api/registry/v1/auth/refresh')
      } catch (err) {
        console.error('Failed to refresh session', err)
      }
    },
    async logout() {
      // Reset Pinia stores
      this.userInfo = null
      
      const activePinia = getActivePinia()
      if (activePinia) {
        useAddressStore().$reset()
        useProductStore().$reset()
        useProjectStore().$reset()
        useTransactionStore().$reset()
      }

      // Clear session storage
      sessionStorage.clear()

      window.location.href = '/api/registry/v1/logout'
    },
  },
})
